/**
 * usePagination — manages offset-based pagination state and derives page math.
 * Pure offset arithmetic; works for both server-side (modal) and client-side
 * (customer table) pagination patterns.
 *
 * Extracted from WorkshopAuditLogModal and CustomerManagement (BON-214 slice 4).
 */
import { useState } from 'react';

export interface UsePaginationResult {
  /** Current zero-based byte offset into the full list. */
  offset: number;
  /** 1-based current page number. */
  currentPage: number;
  /** Total number of pages (minimum 1). */
  pageCount: number;
  /** True when there is a previous page (offset > 0). */
  hasPrev: boolean;
  /** True when there are more items beyond the current page. */
  hasNext: boolean;
  /** Advance one page forward. No-op if already on last page. */
  nextPage: () => void;
  /** Go back one page. No-op if already on first page. */
  prevPage: () => void;
  /** Programmatically set the offset (e.g. reset to 0 on filter change). */
  setOffset: (offset: number) => void;
}

export function usePagination(total: number, pageSize: number): UsePaginationResult {
  const [offset, setOffset] = useState(0);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;
  const hasPrev = offset > 0;
  const hasNext = offset + pageSize < total;

  const nextPage = () => {
    if (hasNext) setOffset((o) => o + pageSize);
  };

  const prevPage = () => {
    if (hasPrev) setOffset((o) => Math.max(0, o - pageSize));
  };

  return { offset, currentPage, pageCount, hasPrev, hasNext, nextPage, prevPage, setOffset };
}
